import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { Markdown } from "@/components/site/Markdown";
import { ShareArticle } from "@/components/site/ShareArticle";
import { Spinner } from "@/components/site/ProtectedRoute";
import { api } from "@/lib/api";
import { useSeo, pageSeo } from "@/lib/seo";

const SITE = "https://digitalnomadnicaragua.com";
const BLOG_SEO = pageSeo("/blog");
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "");
export const imgSrc = (u) => (u?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

function PostCard({ p }) {
  return (
    <Link to={`/blog/${p.slug}`} className="group bg-white rounded-2xl border border-[#0B2A4A]/10 overflow-hidden flex flex-col hover:shadow-xl transition-shadow" data-testid={`blog-card-${p.slug}`}>
      {p.cover_image && <img src={imgSrc(p.cover_image)} alt={p.cover_alt || p.title} className="aspect-video w-full object-cover object-center group-hover:scale-[1.02] transition-transform" loading="lazy" />}
      <div className="p-6 flex flex-col gap-2 flex-1">
        <p className="text-xs font-mono-ui text-slate-500">{fmt(p.published_at)}</p>
        <h2 className="text-lg font-bold text-[#0B2A4A] leading-snug">{p.title}</h2>
        <p className="text-sm text-slate-600 flex-1">{p.excerpt}</p>
        <span className="text-sm font-semibold text-[#D96F28]">Read article →</span>
      </div>
    </Link>
  );
}

export function BlogPage() {
  useSeo(BLOG_SEO);
  const [posts, setPosts] = useState(null);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)).catch(() => setPosts([])); }, []);
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 flex flex-col gap-10" data-testid="blog-page">
      <div className="max-w-2xl">
        <p className="font-mono-ui text-xs tracking-[0.3em] text-[#1A9AA8]">BLOG</p>
        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0B2A4A]">Digital Nomad Nicaragua Blog</h1>
        <p className="mt-4 text-slate-600">Guides on living and working remotely in Nicaragua, building websites with AI, and starting your digital nomad life.</p>
      </div>
      {posts && (posts.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{posts.map((p) => <PostCard key={p.id} p={p} />)}</div>
        : <p className="text-slate-500" data-testid="blog-empty">New articles are coming soon.</p>)}
    </section>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { setPost(null); api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setMissing(true)); }, [slug]);
  const url = `${SITE}/blog/${slug}`;
  useSeo(post ? {
    title: post.title, description: post.meta_description || post.excerpt, path: `/blog/${slug}`,
    image: post.cover_image?.startsWith("/brand/") ? post.cover_image : undefined,
    jsonLd: { "@context": "https://schema.org", "@graph": [
      { "@type": "BlogPosting", headline: post.title, description: post.meta_description, url, mainEntityOfPage: url, keywords: (post.keywords || []).join(", "),
        image: post.cover_image ? (post.cover_image.startsWith("/api/") ? `${SITE}${post.cover_image}` : `${SITE}${post.cover_image}`) : undefined,
        datePublished: post.published_at, dateModified: post.updated_at, wordCount: post.word_count,
        author: { "@type": "Person", name: "Greg Miceli", url: `${SITE}/about-the-host` },
        publisher: { "@type": "Organization", name: "Digital Nomad Nicaragua", logo: { "@type": "ImageObject", url: `${SITE}/brand/logo-square.jpg` } } },
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` }, { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: url }] },
      ...(post.faq?.length ? [{ "@type": "FAQPage", mainEntity: post.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }] : []),
    ] },
  } : { title: "Blog", path: `/blog/${slug}`, noindex: missing });
  if (missing) return <p className="text-center py-24 text-slate-600" data-testid="blog-not-found">Article not found. <Link to="/blog" className="underline">See all articles</Link></p>;
  if (!post) return <Spinner />;
  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-8 py-12 flex flex-col gap-6" data-testid="blog-post">
      <Link to="/blog" className="inline-flex items-center gap-1 text-sm font-semibold text-[#147C88]"><ArrowLeft className="h-4 w-4" /> All articles</Link>
      <h1 className="text-3xl sm:text-5xl font-bold tracking-tight text-[#0B2A4A] leading-tight" data-testid="blog-post-title">{post.title}</h1>
      <p className="flex flex-wrap items-center gap-3 text-sm text-slate-500">By Greg Miceli · {fmt(post.published_at)} <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {Math.max(1, Math.round(post.word_count / 220))} min read</span></p>
      <div><ShareArticle slug={post.slug} title={post.title} /></div>
      {post.cover_image && <img src={imgSrc(post.cover_image)} alt={post.cover_alt || post.title} className="aspect-video w-full object-cover object-center rounded-3xl" data-testid="blog-post-cover" />}
      <Markdown text={post.content} />
      {post.faq?.length > 0 && (
        <section className="mt-6 flex flex-col gap-4" data-testid="blog-post-faq">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0B2A4A]">Frequently asked questions</h2>
          {post.faq.map((f) => <div key={f.q} className="rounded-2xl bg-[#FAF6EE] p-5"><h3 className="font-semibold text-[#0B2A4A]">{f.q}</h3><p className="mt-2 text-slate-700">{f.a}</p></div>)}
        </section>
      )}
      <div className="mt-6 rounded-3xl bg-[#0B2A4A] text-[#F5ECD7] p-8 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
        <p className="text-lg font-semibold text-white">Ready to start your digital nomad journey?</p>
        <div className="flex flex-wrap gap-3">
          <Link to="/digital-nomad-beginners-course" className="rounded-xl bg-[#F08A3C] text-white px-5 py-3 font-semibold" data-testid="blog-cta-nomad">Digital Nomad course</Link>
          <Link to="/ai-website-developer-course" className="rounded-xl border border-white/30 px-5 py-3 font-semibold" data-testid="blog-cta-ai">AI Website course</Link>
        </div>
      </div>
    </article>
  );
}
